import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Row } from "reactstrap";

const Paginations = ({
  total, // ✅ total rows from server
  perPageData, // page size (limit)
  currentPage, // 1-based page
  setCurrentPage, // setter
  isShowingPageLength,
  paginationDiv,
  paginationClass
}) => {
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((total || 0) / (perPageData || 1))),
    [total, perPageData]
  );

  const handleClick = (e) => {
    e.preventDefault();
    const n = Number(e.currentTarget.id);
    if (!Number.isNaN(n) && n >= 1 && n <= totalPages) setCurrentPage(n);
  };

  const handlePrevPage = (e) => {
    e?.preventDefault?.();
    setCurrentPage((p) => Math.max(1, p - 1));
  };

  const handleNextPage = (e) => {
    e?.preventDefault?.();
    setCurrentPage((p) => Math.min(totalPages, p + 1));
  };

  // Clamp current page when total shrinks (e.g. filter); skip while total is unknown (0)
  useEffect(() => {
    if (total > 0 && currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages, total, setCurrentPage]);

  // Build page numbers with windowing (show limited pages with ellipsis)
  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxVisible = 7; // Maximum number of page buttons to show
    const sidePages = 2; // Number of pages to show on each side of current page

    if (totalPages <= maxVisible) {
      // Show all pages if total is small
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    // Always show first page
    pages.push(1);

    // Calculate start and end of the window around current page
    let start = Math.max(2, currentPage - sidePages);
    let end = Math.min(totalPages - 1, currentPage + sidePages);

    // Adjust window if we're near the start
    if (currentPage <= sidePages + 2) {
      end = Math.min(maxVisible - 1, totalPages - 1);
    }

    // Adjust window if we're near the end
    if (currentPage >= totalPages - sidePages - 1) {
      start = Math.max(2, totalPages - (maxVisible - 2));
    }

    // Add ellipsis after first page if needed
    if (start > 2) {
      pages.push("ellipsis-start");
    }

    // Add pages in the window
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    // Add ellipsis before last page if needed
    if (end < totalPages - 1) {
      pages.push("ellipsis-end");
    }

    // Always show last page
    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  }, [totalPages, currentPage]);

  // Showing X–Y of total
  const start = total === 0 ? 0 : (currentPage - 1) * perPageData + 1;
  const end = Math.min(total, currentPage * perPageData);

  return (
    <React.Fragment>
      <Row className="justify-content-between align-items-center">
        {isShowingPageLength && (
          <div className="col-sm">
            <div className="text-muted">
              Showing{" "}
              <span className="fw-semibold">
                {start}-{end}
              </span>{" "}
              of <span className="fw-semibold">{total}</span> entries
            </div>
          </div>
        )}
        <div className={paginationDiv}>
          <ul className={paginationClass}>
            <li className={`page-item ${currentPage <= 1 ? "disabled" : ""}`}>
              <Link className="page-link" to="#" onClick={handlePrevPage}>
                <i className="mdi mdi-chevron-left"></i>
              </Link>
            </li>

            {(pageNumbers || []).map((item, idx) => {
              if (item === "ellipsis-start" || item === "ellipsis-end") {
                return (
                  <li className="page-item disabled" key={`ellipsis-${idx}`}>
                    <span className="page-link">...</span>
                  </li>
                );
              }
              return (
              <li className={`page-item ${currentPage === item ? "active" : ""}`} key={item}>
                <Link className="page-link" to="#" id={item} onClick={handleClick}>
                  {item}
                </Link>
              </li>
              );
            })}

            {/* ✅ Fix: disable Next when on last page */}
            <li className={`page-item ${currentPage >= totalPages ? "disabled" : ""}`}>
              <Link className="page-link" to="#" onClick={handleNextPage}>
                <i className="mdi mdi-chevron-right"></i>
              </Link>
            </li>
          </ul>
        </div>
      </Row>
    </React.Fragment>
  );
};

export default Paginations;
